import {create} from '@github-ui/dotcom-schema/protobuf'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {
  type DevelopmentResponse,
  DevelopmentResponseSchema,
  type LinkedIssue,
  LinkedIssueSchema,
  RepositorySchema,
  IssueState,
  IssueStateReason,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/development'
import type {LegacyLinkedIssue, LegacyDevelopmentData} from '../payloads/development'

/**
 * TEMPORARY — proto migration shim for the `development` page-data endpoint.
 *
 * The generated `DevelopmentResponse` message is the canonical shape that all
 * consumers code against. When the backend `development_proto` flag is ON the
 * endpoint returns proto3-JSON, which `queryFnFetch` decodes (via the
 * `X-Proto-JSON` header + the generated `DevelopmentResponseSchema`) straight into
 * a `DevelopmentResponse` — we pass it through untouched. When the flag is OFF the
 * endpoint returns the legacy JSON shape, which we normalize UP to the proto shape
 * so downstream code only ever sees `DevelopmentResponse`.
 *
 * We always normalize legacy → proto (never the other way around). Once the flag
 * is fully rolled out and the legacy backend branch is removed, delete this whole
 * module and pass `DevelopmentResponseSchema` to the route directly.
 */

function protoIssueState(state: LegacyLinkedIssue['state']): IssueState {
  return state === 'OPEN' ? IssueState.OPEN : IssueState.CLOSED
}

function protoIssueStateReason(stateReason: LegacyLinkedIssue['stateReason']): IssueStateReason {
  switch (stateReason) {
    case 'COMPLETED':
      return IssueStateReason.COMPLETED
    case 'DUPLICATE':
      return IssueStateReason.DUPLICATE
    case 'NOT_PLANNED':
      return IssueStateReason.NOT_PLANNED
    case 'REOPENED':
      return IssueStateReason.REOPENED
    default:
      // Legacy encodes "no recorded reason" as null.
      return IssueStateReason.UNSPECIFIED
  }
}

// Exported for unit tests / mock factories that need a single normalized issue.
export function protoLinkedIssue(issue: LegacyLinkedIssue): LinkedIssue {
  return create(LinkedIssueSchema, {
    id: BigInt(issue.id),
    number: issue.number,
    title: issue.title,
    url: issue.url,
    state: protoIssueState(issue.state),
    stateReason: protoIssueStateReason(issue.stateReason),
    repository: create(RepositorySchema, {
      owner: issue.repository.owner,
      name: issue.repository.name,
    }),
  })
}

// Exported for unit testing; the route consumes `developmentQueryFn` below.
export function normalizeDevelopment(data: LegacyDevelopmentData): DevelopmentResponse {
  return create(DevelopmentResponseSchema, {
    linkedIssues: data.linkedIssues.map(protoLinkedIssue),
  })
}

// Proto-decoded messages carry a `$typeName` discriminator; legacy JSON does not.
function isProtoDevelopment(response: DevelopmentResponse | LegacyDevelopmentData): response is DevelopmentResponse {
  return '$typeName' in response
}

/**
 * `queryFn` for the `development` page-data query. Decodes proto responses against
 * the generated schema and passes them through; normalizes legacy JSON responses
 * up to the proto `DevelopmentResponse` shape.
 */
export async function developmentQueryFn(queryKey: Parameters<typeof queryFnFetch>[0]): Promise<DevelopmentResponse> {
  const response = await queryFnFetch<typeof DevelopmentResponseSchema, LegacyDevelopmentData>(queryKey, {
    schema: DevelopmentResponseSchema,
  })
  return isProtoDevelopment(response) ? response : normalizeDevelopment(response)
}
